import { Facility } from '@reliance/types/models/Facility';
import {
  Navigate,
  Outlet,
  type LoaderFunctionArgs,
  type RouteObject,
  type UIMatch,
} from 'react-router-dom';
import api from '../common/api';
import FacilityDetailView from './FacilityDetailView';
import FacilityMain from './Main';

const facilityRoutes: RouteObject[] = [
  {
    path: '/facilities',
    element: <Outlet />,
    handle: {
      documentTitle: 'Facilities',
      crumb: () => ({ label: 'Facilities', to: '/facilities' }),
    },
    children: [
      { index: true, element: <FacilityMain /> },
      {
        path: 'view/:id',
        element: <Outlet />,
        loader: async ({ params }: LoaderFunctionArgs) => {
          const id = Number(params.id);
          if (!Number.isFinite(id) || id <= 0) {
            throw new Response('Invalid facility id', { status: 400 });
          }
          try {
            const res = await api.get<Facility>(`/facility/${id}?active=false`);
            return res.data;
          } catch (e: unknown) {
            const status = (e as { response?: { status?: number } })?.response
              ?.status;
            if (status === 404) {
              throw new Response(`Facility with ID ${id} not found`, { status: 404 });
            }
            throw e;
          }
        },
        handle: {
          crumb: (match: unknown) => {
            const facility = (match as UIMatch).data as Facility | undefined;
            const label = facility?.name ?? 'Facility';
            return {
              label,
              to: facility?.id != null ? `/facilities/view/${facility.id}` : undefined,
            };
          },
        },
        children: [
          { index: true, element: <FacilityDetailView />, handle: { tabId: 'details' } },
          { path: 'details', element: <FacilityDetailView />, handle: { tabId: 'details' } },
          { path: 'notes', element: <FacilityDetailView />, handle: { tabId: 'notes' } },
          { path: 'devices', element: <FacilityDetailView />, handle: { tabId: 'devices' } },
          { path: 'inmates', element: <FacilityDetailView />, handle: { tabId: 'inmates' } },
          { path: 'housing-units', element: <FacilityDetailView />, handle: { tabId: 'housing-units' } },
          { path: 'stations', element: <FacilityDetailView />, handle: { tabId: 'stations' } },
          { path: 'config', element: <FacilityDetailView />, handle: { tabId: 'config' } },
          { path: 'alerts', element: <FacilityDetailView />, handle: { tabId: 'alerts' } },
          { path: 'special-conditions', element: <FacilityDetailView />, handle: { tabId: 'special-conditions' } },
          { path: 'blocks', element: <FacilityDetailView />, handle: { tabId: 'blocks' } },
          { path: 'equipment', element: <FacilityDetailView />, handle: { tabId: 'equipment' } },
          { path: 'visits', element: <FacilityDetailView />, handle: { tabId: 'visits' } },
          { path: 'schedules', element: <FacilityDetailView />, handle: { tabId: 'schedules' } },
          { path: 'salt-minions', element: <FacilityDetailView />, handle: { tabId: 'salt-minions' } },
          { path: 'analog-gateways', element: <FacilityDetailView />, handle: { tabId: 'analog-gateways' } },
          { path: 'tv-audio', element: <FacilityDetailView />, handle: { tabId: 'tv-audio' } },
          { path: 'numbers', element: <FacilityDetailView />, handle: { tabId: 'numbers' } },
          { path: 'routing', element: <FacilityDetailView />, handle: { tabId: 'routing' } },
          { path: 'configuration-texts', element: <FacilityDetailView />, handle: { tabId: 'configuration-texts' } },
          { path: 'device-groups', element: <FacilityDetailView />, handle: { tabId: 'device-groups' } },
          { path: 'subscriptions', element: <FacilityDetailView />, handle: { tabId: 'subscriptions' } },
          { path: 'games', element: <FacilityDetailView />, handle: { tabId: 'games' } },
          { path: 'trust-account', element: <FacilityDetailView />, handle: { tabId: 'trust-account' } },
          { path: 'visitation-booths', element: <FacilityDetailView />, handle: { tabId: 'visitation-booths' } },
          { path: 'text-message/alerts', element: <FacilityDetailView />, handle: { tabId: 'text-message/alerts' } },
          { path: 'speed-dial/destinations', element: <FacilityDetailView />, handle: { tabId: 'speed-dial/destinations' } },

          // Fallback: unknown paths under /facilities/view/:id go to details.
          { path: '*', element: <Navigate to="" replace /> },
        ],
      },
    ],
  },
];

export default facilityRoutes;
